<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ title }}</h4>
        </div>
      </div>

      <div class="page-btn">
        <router-link :to="{ name: createRoute }" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ createLabel }}
        </router-link>
      </div>
    </div>

    <ThemeDataTable
      :table-id="tableId"
      :fetch-data="fetchVouchersPage"
      :filters="tableFilters"
      :default-order-column="1"
      default-order-dir="desc"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("voucher_no") }}</th>
            <th>{{ $t("voucher_date") }}</th>
            <th>{{ $t("head") }}</th>
            <th>{{ $t("party") }}</th>
            <th>{{ $t("narration") }}</th>
            <th>{{ $t("payment_type") }}</th>
            <th>{{ $t("amount") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.voucher_id }}</td>
            <td>{{ row.voucher_date }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti" :class="headIcon"></i>
                  </span>
                </span>
                <div>
                  <div>{{ row.head_name || '—' }}</div>
                  <small class="text-muted">{{ row.account_id }}</small>
                </div>
              </div>
            </td>
            <td>
              <span v-if="row.party_name">
                <i class="ti me-1" :class="row.party_kind === 'supplier' ? 'ti-truck' : 'ti-user'"></i>{{ row.party_name }}
              </span>
              <span v-else>—</span>
            </td>
            <td>{{ row.narration || '—' }}</td>
            <td>{{ row.payment_name || '—' }}</td>
            <td>{{ formatMoney(row.amount) }}</td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { truncateMoney2 } from '@/utils';

// Debit, credit, journal and contra vouchers are numbered 15YYYYMMDDNNN; other rows with the same
// flag (POS sales, due receipts) are not vouchers entered on these pages.
const VOUCHER_PREFIX = '15';

// thead index -> how that column sorts
const SORT_KEYS = {
  0: (row) => row.voucher_id || '',
  1: (row) => row.voucher_date || '',
  2: (row) => row.head_name || '',
  3: (row) => row.party_name || '',
  4: (row) => row.narration || '',
  5: (row) => row.payment_name || '',
  6: (row) => row.amount,
};

export default {
  name: 'VoucherLedgerList',
  components: { ThemeDataTable },

  props: {
    // 'DR' (debit voucher: expense lines paid from cash/bank) or 'CR' (credit voucher: income lines received).
    flag: { type: String, required: true },
    title: { type: String, required: true },
    createRoute: { type: String, required: true },
    createLabel: { type: String, required: true },
    tableId: { type: String, required: true },
    headIcon: { type: String, default: 'ti-receipt' },
  },

  data() {
    return {
      // The endpoint returns every row at once, so paging, search and sort happen here.
      voucherRows: null,
      loadingRows: null,
    };
  },

  computed: {
    // The head lines are the debit side of a debit voucher and the credit side of a credit voucher;
    // the opposite side is the cash/bank account the money moved through.
    headSide() {
      return this.flag === 'CR' ? 'credit' : 'debit';
    },

    headOptions() {
      return [...new Set((this.voucherRows || []).map((row) => row.head_name).filter(Boolean))];
    },

    paymentOptions() {
      return [...new Set((this.voucherRows || []).map((row) => row.payment_name).filter(Boolean))];
    },

    partyOptions() {
      return [...new Set((this.voucherRows || []).map((row) => row.party_name).filter(Boolean))];
    },

    tableFilters() {
      return [
        { key: 'head', label: this.$t('head'), type: 'select2', options: this.headOptions },
        { key: 'party', label: this.$t('party'), type: 'select2', options: this.partyOptions },
        { key: 'payment', label: this.$t('payment_type'), type: 'select2', options: this.paymentOptions },
      ];
    },
  },

  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    loadRows() {
      if (!this.loadingRows) {
        this.loadingRows = Promise.all([
          this.$axios.get(apiUrls.journal_voucher.get, {
            params: { flag: this.flag, transaction_status: 'AV' },
          }),
          this.$axios.get(apiUrls.chart_of_accounts.get),
          this.$axios.get(apiUrls.customers.get),
          this.$axios.get(apiUrls.inventory_supplier.get),
        ])
          .then(([ledgerRes, headsRes, customersRes, suppliersRes]) => {
            this.voucherRows = this.buildRows(
              ledgerRes.data || [], headsRes.data || [], customersRes.data || [], suppliersRes.data || []
            );
          })
          .catch((error) => {
            this.loadingRows = null;
            throw error;
          });
      }
      return this.loadingRows;
    },

    buildRows(ledger, heads, customers, suppliers) {
      const headNames = new Map(heads.map((h) => [String(h.account_id), (h.node || '').trim()]));
      const customerNames = new Map(customers.map((c) => [c.id, c.customer_name]));
      const supplierNames = new Map(suppliers.map((s) => [s.id, s.name]));
      const otherSide = this.headSide === 'debit' ? 'credit' : 'debit';

      const lines = ledger.filter((row) => String(row.voucher_id || '').startsWith(VOUCHER_PREFIX));
      const paymentByVoucher = new Map();
      lines.forEach((row) => {
        if (Number(row[otherSide]) > 0 && !paymentByVoucher.has(row.voucher_id)) {
          paymentByVoucher.set(row.voucher_id, headNames.get(String(row.account_id)) || String(row.account_id));
        }
      });

      return lines
        .filter((row) => Number(row[this.headSide]) > 0)
        .map((row) => {
          const party = row.pos_customer_id
            ? { party_kind: 'customer', party_name: customerNames.get(row.pos_customer_id) || '' }
            : row.supplier_id
              ? { party_kind: 'supplier', party_name: supplierNames.get(row.supplier_id) || '' }
              : { party_kind: '', party_name: '' };
          return {
            ...row,
            ...party,
            head_name: headNames.get(String(row.account_id)) || '',
            payment_name: paymentByVoucher.get(row.voucher_id) || '',
            amount: Number(row[this.headSide]) || 0,
          };
        });
    },

    async fetchVouchersPage({ start, length, search, orderColumn, orderDir, filters }) {
      await this.loadRows();
      const all = this.voucherRows;
      let rows = all;

      if (filters.head) rows = rows.filter((row) => row.head_name === filters.head);
      if (filters.party) rows = rows.filter((row) => row.party_name === filters.party);
      if (filters.payment) rows = rows.filter((row) => row.payment_name === filters.payment);

      const term = (search || '').toLowerCase();
      if (term) {
        rows = rows.filter((row) =>
          [row.voucher_id, row.voucher_date, row.head_name, row.account_id, row.party_name, row.narration, row.payment_name]
            .some((value) => String(value ?? '').toLowerCase().includes(term))
        );
      }

      const key = SORT_KEYS[orderColumn] || SORT_KEYS[1];
      const dir = orderDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const x = key(a);
        const y = key(b);
        const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
        return cmp * dir || (b.id - a.id);
      });

      return {
        data: rows.slice(start, start + length),
        recordsTotal: all.length,
        recordsFiltered: rows.length,
      };
    },
  },
};
</script>
